import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { AlertCircle, ArrowRightLeft, Clock3, Leaf, PackageCheck, RefreshCw, Send, Sprout, Trees, Warehouse } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { accountingApi } from "@/features/accounting/services/accountingApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { isBusinessDateToday } from "@/lib/businessDate";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useDashboardOverview, useManagementAccounting, useManagementActivities, useManagementInvoicedSummary, useManagementInvoicedTrend, useManagementProductSettlement } from "../hooks/useDashboardOverview";
import { dashboardApi } from "../services/dashboardApi";
import type { ManagementAccountingSnapshot, ManagementActivity, ManagementProductSales, SeedlingStagesOverview } from "../services/dashboardApi";

const number = new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 });
const compactMoney = new Intl.NumberFormat("en-KE", { notation: "compact", maximumFractionDigits: 1 });

const STAGE_LABELS: Array<{ key: keyof SeedlingStagesOverview; label: string; color: string }> = [
  { key: "pendingOrIssued", label: "Pending or Issued", color: "bg-emerald-700" },
  { key: "inSowing", label: "In Sowing", color: "bg-lime-600" },
  { key: "germinated", label: "Germinated", color: "bg-green-500" },
  { key: "growing", label: "Growing", color: "bg-amber-500" },
  { key: "readyForDispatch", label: "Ready for Dispatch", color: "bg-teal-600" },
];

const formatAsOf = (value?: string) => value
  ? new Date(value).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Nairobi" })
  : "-";

export const Dashboard = () => {
  const canViewManagement = useAuthStore((state) => state.hasPermission("dashboard.view_management"));
  const overviewQuery = useDashboardOverview();
  const companiesQuery = useQuery({ queryKey: ["companies"], queryFn: accountingApi.getCompanies, enabled: canViewManagement });
  const activeCompany = companiesQuery.data?.find(company => company.isActive);
  const companyId = activeCompany?.id ?? "";
  const accountingQuery = useManagementAccounting(companyId, canViewManagement);
  const settlementQuery = useManagementProductSettlement(companyId, canViewManagement);
  const summaryQuery = useManagementInvoicedSummary(companyId, canViewManagement);
  const trendQuery = useManagementInvoicedTrend(companyId, canViewManagement);
  const activitiesQuery = useManagementActivities(companyId, !canViewManagement || !!companyId);
  const managementQueries = [accountingQuery, settlementQuery, summaryQuery, trendQuery];
  const refresh = async () => {
    if (canViewManagement && companyId) await dashboardApi.refreshManagement(companyId);
    await Promise.all([
      overviewQuery.refetch(),
      ...(!canViewManagement || companyId ? [activitiesQuery.refetch()] : []),
      ...(canViewManagement ? managementQueries.map(query => query.refetch()) : []),
    ]);
  };
  const isRefreshing = overviewQuery.isFetching || activitiesQuery.isFetching || managementQueries.some(query => query.isFetching);

  return <div className="space-y-5">
    <div className="rounded-xl border bg-gradient-to-r from-emerald-950 via-emerald-800 to-lime-700 p-5 text-white shadow-sm sm:flex sm:items-center sm:justify-between">
      <div><p className="text-sm text-emerald-100">Management and operational snapshot</p><h1 className="text-2xl font-semibold tracking-tight">{activeCompany?.companyName ?? "Seedlings Propagation Company"}</h1><div className="mt-2 flex items-center gap-2 text-xs text-emerald-100"><Clock3 className="h-3.5 w-3.5" />As of {formatAsOf(overviewQuery.data?.asOf ?? accountingQuery.data?.asOf)}</div></div>
      <Button variant="secondary" size="sm" className="mt-3 sm:mt-0" onClick={refresh} disabled={isRefreshing}><RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />Refresh</Button>
    </div>
    {canViewManagement && <ManagementSections
      accountingQuery={accountingQuery}
      settlementQuery={settlementQuery}
      summaryQuery={summaryQuery}
      trendQuery={trendQuery}
    />}
    {overviewQuery.isLoading && <SectionSkeleton className="h-80" />}
    {overviewQuery.isError && <DashboardError error={overviewQuery.error} retry={() => overviewQuery.refetch()} />}
    {overviewQuery.data && <OperationalCapacity data={overviewQuery.data} />}
    <SectionState query={activitiesQuery} title="Daily operational activity">
      {activitiesQuery.data && <Activities activities={activitiesQuery.data.activities} />}
    </SectionState>
  </div>;
};

function ManagementSections({ accountingQuery, settlementQuery, summaryQuery, trendQuery }: {
  accountingQuery: ReturnType<typeof useManagementAccounting>;
  settlementQuery: ReturnType<typeof useManagementProductSettlement>;
  summaryQuery: ReturnType<typeof useManagementInvoicedSummary>;
  trendQuery: ReturnType<typeof useManagementInvoicedTrend>;
}) {
  const metadata = accountingQuery.data ?? settlementQuery.data ?? summaryQuery.data ?? trendQuery.data;
  const isLive = metadata ? isBusinessDateToday(metadata.asOf, metadata.businessTimezone) : true;

  return <section className="space-y-4">
    <Badge variant="outline" className={cn("w-fit", isLive && "border-emerald-600/40 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300")}>
      {isLive ? "Live today - updates as transactions are posted" : "Historical date"}
    </Badge>
    <SectionState query={accountingQuery} title="Today’s accounting position">
      {accountingQuery.data?.accounting && <AccountingPosition accounting={accountingQuery.data.accounting} currency={accountingQuery.data.currency} asOf={accountingQuery.data.asOf} />}
    </SectionState>
    <div className="grid gap-4 xl:grid-cols-12">
      <div className="xl:col-span-5 xl:row-span-2">
        <SectionState query={settlementQuery} title="Daily product settlement">
          {settlementQuery.data && <ProductSettlement
            products={settlementQuery.data.products}
            currency={settlementQuery.data.currency}
            asOf={settlementQuery.data.asOf}
            cashReceivedToday={accountingQuery.data?.accounting?.postedCashReceivedToday}
          />}
        </SectionState>
      </div>
      <div className="xl:col-span-3">
        <SectionState query={summaryQuery} title="Weekly and monthly invoiced value">
          {summaryQuery.data && <Card><CardHeader><CardTitle>Weekly &amp; Monthly Invoiced Value</CardTitle><CardDescription>Invoice value by invoice date; cash received is reported separately</CardDescription></CardHeader><CardContent className="grid gap-3"><SalesPeriodTile label="This week" amount={summaryQuery.data.weeklySales.grossSalesAmount} trays={summaryQuery.data.weeklySales.trayEquivalent} currency={summaryQuery.data.currency} /><SalesPeriodTile label="This month" amount={summaryQuery.data.monthlySales.grossSalesAmount} trays={summaryQuery.data.monthlySales.trayEquivalent} currency={summaryQuery.data.currency} /></CardContent></Card>}
        </SectionState>
      </div>
      <div className="xl:col-span-4">
        <SectionState query={trendQuery} title="Month-to-month invoiced value">
          {trendQuery.data && <InvoicedTrend data={trendQuery.data.monthlyComparison} currency={trendQuery.data.currency} />}
        </SectionState>
      </div>
    </div>
  </section>;
}

function ProductSettlement({ products, currency, asOf, cashReceivedToday }: { products: ManagementProductSales[]; currency: string; asOf: string; cashReceivedToday?: number | null }) {
  const money = (value: number) => `${currency} ${number.format(value)}`;
  const dailyTotal = products.reduce((sum, item) => sum + item.salesAmount, 0);
  const settlementTotal = products.reduce((sum, item) => sum + (item.settlementAllocatedAmount ?? 0), 0);
  const receivableTotal = products.reduce((sum, item) => sum + (item.tradeReceivableAmount ?? 0), 0);
  const hasSettlementAllocation = products.some(item => item.settlementAllocatedAmount != null);
  return <Card><CardHeader><CardTitle>Daily Product Settlement</CardTitle><CardDescription>Invoice value, settlement allocated, and trade receivable by variety on {asOf}.</CardDescription></CardHeader><CardContent className="space-y-3">
        {products.length === 0 ? <EmptyState label="No eligible sales today" /> : <div className="overflow-x-auto">
          <Table className="min-w-[460px] text-xs">
            <TableHeader><TableRow><TableHead>Variety</TableHead><TableHead className="text-right">Invoiced</TableHead><TableHead className="text-right">Settlement allocated</TableHead><TableHead className="text-right">Trade receivable</TableHead></TableRow></TableHeader>
            <TableBody>{products.slice(0, 10).map(item => <TableRow key={`${item.productCategory}-${item.productName}`}><TableCell><span className="block font-medium">{item.productName}</span><span className="text-muted-foreground">{item.productCategory}</span></TableCell><TableCell className="text-right tabular-nums">{money(item.salesAmount)}</TableCell><TableCell className="text-right tabular-nums text-emerald-700">{item.settlementAllocatedAmount == null ? "—" : money(item.settlementAllocatedAmount)}</TableCell><TableCell className="text-right font-medium tabular-nums text-amber-700">{item.tradeReceivableAmount == null ? "—" : money(item.tradeReceivableAmount)}</TableCell></TableRow>)}</TableBody>
            <TableFooter><TableRow><TableCell>Total</TableCell><TableCell className="text-right tabular-nums">{money(dailyTotal)}</TableCell><TableCell className="text-right tabular-nums text-emerald-700">{hasSettlementAllocation ? money(settlementTotal) : "—"}</TableCell><TableCell className="text-right tabular-nums text-amber-700">{hasSettlementAllocation ? money(receivableTotal) : "—"}</TableCell></TableRow></TableFooter>
          </Table>
        </div>}
        {hasSettlementAllocation && cashReceivedToday !== undefined && <SettlementCashReconciliation settled={settlementTotal} cashReceived={cashReceivedToday} currency={currency} />}
        <p className="border-t pt-3 text-xs text-muted-foreground">Settlement is allocated pro rata using immutable invoice-line value. Receipts legally settle the customer invoice, not a specific variety.</p>
      </CardContent></Card>;
}

function InvoicedTrend({ data, currency }: { data: Array<{ monthLabel: string; currentYear: number; currentYearSales: number; previousYear: number; previousYearSales: number }>; currency: string }) {
  const money = (value: number) => `${currency} ${number.format(value)}`;
  return <Card><CardHeader><CardTitle>Month-to-Month Invoiced Value</CardTitle><CardDescription>Invoice value by month; cash received is reported separately</CardDescription></CardHeader><CardContent className="h-64"><ResponsiveContainer width="100%" height="100%"><LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="monthLabel" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(value) => compactMoney.format(Number(value))} width={45} /><ChartTooltip formatter={(value) => money(Number(value))} /><Legend /><Line type="monotone" dataKey="previousYearSales" name={String(data[0]?.previousYear ?? "Previous")} stroke="#84a83f" strokeWidth={2} dot={{ r: 2 }} /><Line type="monotone" dataKey="currentYearSales" name={String(data[0]?.currentYear ?? "Current")} stroke="#166534" strokeWidth={3} dot={{ r: 3 }} /></LineChart></ResponsiveContainer></CardContent></Card>;
}

function Activities({ activities }: { activities: ManagementActivity[] }) {
  const activity = (code: string) => activities.find(item => item.code === code);
  return <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
    <ActivityCard title="Seedlings Transfers" icon={<ArrowRightLeft className="h-5 w-5" />} activity={activity("greenhouse-transfers")} />
    <ActivityCard title="Overgrowns & Losses" icon={<Leaf className="h-5 w-5" />} activity={activity("overgrown")} secondary="Monetary loss is unavailable without a verified cost basis." tone="danger" />
    <ActivityCard title="Today's Dispatches" icon={<Send className="h-5 w-5" />} activity={activity("dispatches")} />
    <ActivityCard title="Daily Sowings" icon={<Sprout className="h-5 w-5" />} activity={activity("sowings")} />
    <ActivityCard title="Gapping Done" icon={<Sprout className="h-5 w-5" />} activity={activity("gappings")} secondary="A transaction workflow is required before this can report a value." />
  </div>;
}

function AccountingPosition({ accounting, currency, asOf }: { accounting: ManagementAccountingSnapshot; currency: string; asOf: string }) {
  const money = (value: number) => `${currency} ${number.format(value)}`;
  const methods = accounting.dailySalesByPaymentMethod;
  const standardMethods = ["CASH", "MPESA", "EQUITY", "KCB", "ABSA"];
  const rows = methods == null ? [] : [
    ...standardMethods.map(paymentMethod => ({ paymentMethod, amount: methods.find(item => item.paymentMethod === paymentMethod)?.amount ?? 0 })),
    ...methods.filter(item => !standardMethods.includes(item.paymentMethod)),
  ];
  const date = asOf.split("T")[0].split("-").reverse().join("/");

  return <Card className="overflow-hidden border-emerald-700/20">
    <CardContent className="p-0">
      {methods == null ? <div className="p-6"><EmptyState label="Daily sales unavailable" /></div> : <div className="grid md:grid-cols-5">
        <div className="flex flex-col justify-center gap-3 border-b bg-emerald-50/70 p-6 dark:bg-emerald-950/20 md:col-span-2 md:border-b-0 md:border-r lg:p-8">
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Total Sales today</p>
          <p className="text-3xl font-semibold tracking-tight tabular-nums lg:text-4xl">{money(methods.reduce((sum, item) => sum + item.amount, 0))}</p>
          <p className="text-sm font-medium text-muted-foreground">{date}</p>
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">Payments approved today, including partial payments for earlier orders.</p>
        </div>
        <div className="p-5 md:col-span-3 lg:px-8">
          <h3 className="mb-3 text-sm font-semibold">Payment breakdown</h3>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="h-8 pl-0 text-xs">Payment method</TableHead><TableHead className="h-8 pr-0 text-right text-xs">Amount received</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map(item => <TableRow key={item.paymentMethod} className="hover:bg-transparent"><TableCell className={cn("py-2 pl-0 font-medium", item.amount === 0 && "text-muted-foreground")}>{item.paymentMethod}</TableCell><TableCell className={cn("py-2 pr-0 text-right font-medium tabular-nums", item.amount === 0 && "text-muted-foreground")}>{money(item.amount)}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      </div>}
    </CardContent>
  </Card>;
}

function SettlementCashReconciliation({ settled, cashReceived, currency }: { settled: number; cashReceived: number | null; currency: string }) {
  const money = (value: number | null) => value == null ? "Unavailable" : `${currency} ${number.format(value)}`;
  const difference = cashReceived == null ? null : settled - cashReceived;
  const direction = difference == null
    ? "Cash received is unavailable, so the difference cannot be calculated."
    : Math.abs(difference) < 0.005
      ? "Settlement allocated and cash received match today."
      : difference > 0
        ? `Settlement allocated is ${money(Math.abs(difference))} higher than posted receipts today.`
        : `Posted receipts today is ${money(Math.abs(difference))} higher than settlement allocated.`;

  return <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-800 dark:bg-slate-950/30">
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div>
        <h3 className="text-sm font-semibold">Why settlement and cash are different</h3>
        <p className="text-xs text-muted-foreground">A timing and scope comparison, not an unexplained cash balance.</p>
      </div>
      <Badge variant="outline" className="w-fit bg-background text-muted-foreground">Different date basis</Badge>
    </div>
    <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-stretch">
      <ReconciliationValue label="Settlement allocated" value={money(settled)} note="Applied to today’s invoiced products" className="border-sky-200 bg-sky-50/70 dark:border-sky-900 dark:bg-sky-950/20" />
      <ReconciliationOperator>−</ReconciliationOperator>
      <ReconciliationValue label="Posted receipts today" value={money(cashReceived)} note="Receipts posted today, net of refunds" className="border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/20" />
      <ReconciliationOperator>=</ReconciliationOperator>
      <ReconciliationValue label="Timing & scope difference" value={money(difference == null ? null : Math.abs(difference))} note={difference == null ? "Unavailable" : Math.abs(difference) < 0.005 ? "No difference" : difference > 0 ? "Settlement is higher" : "Cash received is higher"} className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20" />
    </div>
    <p className="mt-3 text-xs font-medium text-foreground">{direction}</p>
    <p className="mt-1 text-xs text-muted-foreground">Settlement may include receipts or credits posted earlier. Posted receipts today may be applied to invoices from earlier dates.</p>
  </div>;
}

function ReconciliationValue({ label, value, note, className }: { label: string; value: string; note: string; className?: string }) {
  return <div className={cn("rounded-lg border p-3", className)}>
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
    <p className="mt-1 text-xs text-muted-foreground">{note}</p>
  </div>;
}

function ReconciliationOperator({ children }: { children: ReactNode }) {
  return <div className="hidden items-center justify-center px-1 text-lg font-semibold text-muted-foreground sm:flex" aria-hidden="true">{children}</div>;
}

function SalesPeriodTile({ label, amount, trays, currency }: { label: string; amount: number; trays: number; currency: string }) {
  return <div className="rounded-xl border bg-gradient-to-b from-lime-50 to-background p-4 dark:from-emerald-950/30"><p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">{label}</p><p className="mt-2 text-2xl font-semibold">{number.format(trays)} <span className="text-xs font-normal text-muted-foreground">tray eq.</span></p><p className="mt-1 text-sm text-muted-foreground">{currency} {number.format(amount)}</p></div>;
}

function ActivityCard({ title, icon, activity, secondary, tone, className }: { title: string; icon: ReactNode; activity?: ManagementActivity; secondary?: string; tone?: "danger"; className?: string }) {
  const unavailable = !activity || activity.daily.availability === 3;
  return <Card className={className}><CardHeader className="pb-2"><CardDescription className={cn("flex items-center gap-2", tone === "danger" && "text-destructive")}>{icon}{title}</CardDescription><CardTitle className="text-2xl">{unavailable ? "Not tracked" : formatActivityQuantity(activity.daily)}</CardTitle></CardHeader><CardContent className="space-y-1 text-xs text-muted-foreground">{secondary && <p>{secondary}</p>}</CardContent></Card>;
}

function formatActivityQuantity(value: ManagementActivity["daily"]) {
  if (value.trayEquivalent != null && value.trayEquivalent !== 0) return `${number.format(value.trayEquivalent)} tray eq.`;
  if (value.pieceCount != null && value.pieceCount !== 0) return `${number.format(value.pieceCount)} pcs`;
  return "0";
}

function OperationalCapacity({ data }: { data: NonNullable<ReturnType<typeof useDashboardOverview>["data"]> }) {
  const totalStageCount = Object.values(data.seedlingStages).reduce((sum, value) => sum + value, 0);
  const greenhouse = data.greenhouseUtilization;
  const inventoryGroups = [data.saleableInventory.vegetables, data.saleableInventory.agroforestry];

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Operational Capacity &amp; Pipeline</h2>
        <p className="text-sm text-muted-foreground">The original operational controls remain available below the management snapshot.</p>
      </div>

      <div className="grid gap-4 xl:grid-cols-4">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-emerald-700" />Saleable Inventory</CardTitle>
            <CardDescription>Dispatch-ready stock by native unit</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {inventoryGroups.map((group, index) => (
              <div key={group.label} className="rounded-lg border p-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {index === 0 ? <Sprout className="h-4 w-4 text-emerald-600" /> : <Trees className="h-4 w-4 text-amber-700" />}
                  {group.label}
                </div>
                <p className="mt-2 text-2xl font-semibold">{number.format(group.available)} <span className="text-sm font-normal text-muted-foreground">{group.unit}</span></p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <Stat label="Reserved" value={group.reserved} />
                  <Stat label="Dispatch-ready" value={group.dispatchReady} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription className="flex items-center gap-2"><Warehouse className="h-4 w-4 text-teal-600" />Greenhouse Utilization</CardDescription>
            <CardTitle className="text-3xl">{number.format(greenhouse.occupancyPct)}%</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
            <Stat label="Total" value={greenhouse.total} />
            <Stat label="Occupied" value={greenhouse.occupied} />
            <Stat label="Available" value={greenhouse.available} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardDescription className="flex items-center gap-2"><Leaf className="h-4 w-4 text-destructive" />Overgrown Vegetable Seedlings</CardDescription>
            <CardTitle className="text-3xl">{number.format(data.overgrownSeedlings)} <span className="text-sm font-normal">trays</span></CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">Requires allocation, dispatch, or discard review</CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Vegetable Production Pipeline</CardTitle>
            <CardDescription>Tray-based greenhouse operations</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {STAGE_LABELS.map((stage) => {
              const value = data.seedlingStages[stage.key];
              const percent = totalStageCount > 0 ? value / totalStageCount * 100 : 0;
              return (
                <div key={stage.key} className="space-y-2">
                  <div className="flex justify-between text-sm"><span>{stage.label}</span><Badge variant="secondary">{number.format(value)} trays</Badge></div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted"><div className={cn("h-full rounded-full", stage.color)} style={{ width: `${percent}%` }} /></div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Trees className="h-5 w-5 text-amber-700" />Agroforestry Pipeline</CardTitle>
            <CardDescription>Piece-based stock</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Stat label="Healing / not dispatch-ready" value={data.agroforestryStages.healing} suffix="pieces" />
            <Stat label="Dispatch-ready" value={data.agroforestryStages.dispatchReady} suffix="pieces" />
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number; suffix?: string }) { return <div><span className="text-xs text-muted-foreground">{label}</span><strong className="block text-base">{number.format(value)} {suffix}</strong></div>; }
function EmptyState({ label }: { label: string }) { return <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{label}</div>; }
function SectionSkeleton({ className }: { className?: string }) { return <Card><CardContent className="space-y-3 p-6"><Skeleton className="h-5 w-48" /><Skeleton className={cn("h-40 w-full", className)} /></CardContent></Card>; }
function SectionState({ query, title, children }: { query: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown }; title: string; children: ReactNode }) {
  if (query.isLoading) return <SectionSkeleton />;
  if (query.isError) return <Card className="border-destructive/40 bg-destructive/5"><CardHeader><CardTitle className="flex items-center gap-2 text-base text-destructive"><AlertCircle className="h-4 w-4" />{title} unavailable</CardTitle><CardDescription>{query.error instanceof Error ? query.error.message : "This section could not be loaded."}</CardDescription></CardHeader><CardContent><Button onClick={() => query.refetch()} variant="outline" size="sm">Retry</Button></CardContent></Card>;
  return children;
}
function DashboardError({ error, retry }: { error: unknown; retry: () => void }) { return <Card className="border-destructive/40 bg-destructive/5"><CardHeader><CardTitle className="flex items-center gap-2 text-destructive"><AlertCircle className="h-4 w-4" />Dashboard overview unavailable</CardTitle><CardDescription>{error instanceof Error ? error.message : "We could not load the latest dashboard stats."}</CardDescription></CardHeader><CardContent><Button onClick={retry} variant="outline" size="sm">Retry</Button></CardContent></Card>; }
